<script setup>
import FormGroup from '@maison/components/form/FormGroup.vue';
import RadioListField from '@maison/components/form/fields/RadioListField.vue';
import { PhxStrapFinderSlider } from '@phx-styleguides/iwc';
import { computed, inject } from 'vue';
import { emits as defaultEmits, props as defaultProps, useSection } from '@maison/composables/strapFinder/useSection';
import ConfiguratorSectionLayout from '@maison/components/strapFinder/sections/ConfiguratorSectionLayout.vue';

const props = defineProps({
  ...defaultProps,
  pinBuckleImages: {
    type: Array,
    required: true,
  },
  foldingClaspsImages: {
    type: Array,
    required: true,
  },
});

defineEmits(defaultEmits);

const { id, preTitle, nextSectionId } = useSection('buckle-type');

const currentBuckleType = defineModel('currentBuckleType', {
  type: String,
  required: true,
});

const straps = inject('straps', []);

const buckleTypeField = computed(() => {
  // We are grouping the straps by buckle type. We assume the label and code are unique for each buckle type.
  // we only the get the strap types for each strap as we display them in the additional content of the option.
  const groupedStraps = straps.value.reduce((acc, { buckleType, type, isSteelStrap }) => {
    if (isSteelStrap) return acc;
    const { code, label } = buckleType;
    acc[code] = acc[code] || { label, strapTypes: [] };
    acc[code].strapTypes.push(type.label);
    return acc;
  }, {});

  return {
    id: id.value,
    name: id.value,
    label: '',
    options: Object.entries(groupedStraps).map(([code, { label, strapTypes }]) => ({
      id: code,
      value: code,
      label,
      additionalContent: [...new Set(strapTypes)].sort().join(' / '),
      media: {
        id: code,
        captionText: label,
        // For now, we assume there are only 2 buckle types and as we are not sure about the exact code, we do like that
        pictures: code.toLowerCase().includes('folding') ? props.foldingClaspsImages : props.pinBuckleImages,
      },
    })),
  };
});
</script>

<template>
  <configurator-section-layout
    :id="id"
    :title="title"
    :description="description"
    :memo="memo"
    :pre-title="preTitle"
    :next-section-id="nextSectionId"
  >
    <template #media>
      <phx-strap-finder-slider v-for="option in buckleTypeField.options" :key="option.value" v-bind="option.media" />
    </template>

    <template #default="{ onShowMedia }">
      <form @submit.prevent.stop @change="$emit('onStepValidate', $event, id, onShowMedia)">
        <form-group :attrs="buckleTypeField">
          <radio-list-field v-model="currentBuckleType" type="rich" />
        </form-group>
      </form>
    </template>
  </configurator-section-layout>
</template>
